import { listCompletedActivityHistoryRecords, type CompletedActivityRecord } from "./completedActivityHistory";

export type IntelligenceMetricName =
  | "averageIf"
  | "averageNp"
  | "averagePower"
  | "averageDecoupling"
  | "averageDurability"
  | "averageVariabilityIndex"
  | "averageRespiratoryDrift"
  | "averageRideConfidence";

export type TrendDirection = "improving" | "stable" | "declining";

export type MetricDirectionPreference = "higher_better" | "lower_better";

export type RollingMetricAverages = {
  last7Rides: Partial<Record<IntelligenceMetricName, number | null>>;
  last30Days: Partial<Record<IntelligenceMetricName, number | null>>;
  last90Days: Partial<Record<IntelligenceMetricName, number | null>>;
};

export type HistoricalMetrics = {
  averageIf: number | null;
  averageNp: number | null;
  averagePower: number | null;
  averageDecoupling: number | null;
  averageDurability: number | null;
  averageVariabilityIndex: number | null;
  averageRespiratoryDrift: number | null;
  averageRideConfidence: number | null;
};

export type ComparisonResult = {
  metric: IntelligenceMetricName;
  currentValue: number | null;
  historicalValue: number | null;
  absoluteDelta: number | null;
  percentDelta: number | null;
  directionVsHistorical: "above" | "below" | "aligned" | "insufficient";
};

export type HighestConfidenceMetric = {
  metric: IntelligenceMetricName;
  confidenceScore: number;
  sampleCount: number;
};

export type AthleteIntelligenceSnapshot = {
  rideCount: number;
  lastAnalysisDate: string | null;
  historicalMetrics: HistoricalMetrics;
  rollingAverages: RollingMetricAverages;
  currentTrend: TrendDirection;
  longTermTrend: TrendDirection;
  highestConfidenceMetrics: HighestConfidenceMetric[];
  comparisons: ComparisonResult[];
};

const METRICS: Array<{
  key: IntelligenceMetricName;
  direction: MetricDirectionPreference;
  getter: (record: CompletedActivityRecord) => number | null;
  minSamplesForTrend: number;
  conservativeRelativeThreshold: number;
  conservativeAbsoluteThreshold: number;
}> = [
  {
    key: "averageIf",
    direction: "higher_better",
    getter: record => record.intensityFactor,
    minSamplesForTrend: 8,
    conservativeRelativeThreshold: 0.03,
    conservativeAbsoluteThreshold: 0.02
  },
  {
    key: "averageNp",
    direction: "higher_better",
    getter: record => record.normalizedPower,
    minSamplesForTrend: 8,
    conservativeRelativeThreshold: 0.03,
    conservativeAbsoluteThreshold: 8
  },
  {
    key: "averagePower",
    direction: "higher_better",
    getter: record => record.averagePower,
    minSamplesForTrend: 8,
    conservativeRelativeThreshold: 0.03,
    conservativeAbsoluteThreshold: 8
  },
  {
    key: "averageDecoupling",
    direction: "lower_better",
    getter: record => (record.aerobicDecoupling === null ? null : Math.abs(record.aerobicDecoupling)),
    minSamplesForTrend: 8,
    conservativeRelativeThreshold: 0.12,
    conservativeAbsoluteThreshold: 0.8
  },
  {
    key: "averageDurability",
    direction: "higher_better",
    getter: record => {
      const durabilityValues = [record.durability60, record.durability90, record.durability120].filter((value): value is number => value !== null && Number.isFinite(value));
      if (durabilityValues.length === 0) return null;
      return average(durabilityValues);
    },
    minSamplesForTrend: 8,
    conservativeRelativeThreshold: 0.08,
    conservativeAbsoluteThreshold: 1.2
  },
  {
    key: "averageVariabilityIndex",
    direction: "lower_better",
    getter: record => record.variabilityIndex,
    minSamplesForTrend: 8,
    conservativeRelativeThreshold: 0.02,
    conservativeAbsoluteThreshold: 0.03
  },
  {
    key: "averageRespiratoryDrift",
    direction: "lower_better",
    getter: record => (record.respiratorySummary.respiratoryDriftPercent === null ? null : Math.abs(record.respiratorySummary.respiratoryDriftPercent)),
    minSamplesForTrend: 8,
    conservativeRelativeThreshold: 0.12,
    conservativeAbsoluteThreshold: 1.5
  },
  {
    key: "averageRideConfidence",
    direction: "higher_better",
    getter: record => record.analysisConfidence.overallPercent,
    minSamplesForTrend: 8,
    conservativeRelativeThreshold: 0.03,
    conservativeAbsoluteThreshold: 2
  }
];

function average(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function toRounded(value: number | null, decimals = 3): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function metricAverage(records: CompletedActivityRecord[], getter: (record: CompletedActivityRecord) => number | null): number | null {
  const values = records
    .map(getter)
    .filter((value): value is number => value !== null && Number.isFinite(value));

  if (values.length === 0) {
    return null;
  }
  return toRounded(average(values));
}

function toTimestamp(record: CompletedActivityRecord): number {
  const parsed = Date.parse(record.activityDate);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function dedupeByStableSourceHash(records: CompletedActivityRecord[]): CompletedActivityRecord[] {
  const byHash = new Map<string, CompletedActivityRecord>();

  for (const record of records) {
    const key = record.duplicateIdentity.stableSourceHash || record.activityId;
    const existing = byHash.get(key);
    if (!existing) {
      byHash.set(key, record);
      continue;
    }

    const existingUpdated = Date.parse(existing.updatedAt);
    const nextUpdated = Date.parse(record.updatedAt);
    if (nextUpdated > existingUpdated) {
      byHash.set(key, record);
    }
  }

  return [...byHash.values()].sort((left, right) => toTimestamp(right) - toTimestamp(left));
}

function rollingWindows(records: CompletedActivityRecord[]): {
  last7Rides: CompletedActivityRecord[];
  last30Days: CompletedActivityRecord[];
  last90Days: CompletedActivityRecord[];
} {
  const sorted = [...records].sort((left, right) => toTimestamp(right) - toTimestamp(left));
  const latestTimestamp = toTimestamp(sorted[0]);

  const daysBack = (days: number) => latestTimestamp - days * 24 * 60 * 60 * 1000;
  const cutoff30 = daysBack(30);
  const cutoff90 = daysBack(90);

  return {
    last7Rides: sorted.slice(0, 7),
    last30Days: sorted.filter(record => toTimestamp(record) >= cutoff30),
    last90Days: sorted.filter(record => toTimestamp(record) >= cutoff90)
  };
}

function buildHistoricalMetrics(records: CompletedActivityRecord[]): HistoricalMetrics {
  const result: Partial<HistoricalMetrics> = {};
  for (const metric of METRICS) {
    result[metric.key] = metricAverage(records, metric.getter);
  }
  return result as HistoricalMetrics;
}

function buildRollingMetricAverages(input: ReturnType<typeof rollingWindows>): RollingMetricAverages {
  const result: RollingMetricAverages = {
    last7Rides: {},
    last30Days: {},
    last90Days: {}
  };

  for (const metric of METRICS) {
    result.last7Rides[metric.key] = metricAverage(input.last7Rides, metric.getter);
    result.last30Days[metric.key] = metricAverage(input.last30Days, metric.getter);
    result.last90Days[metric.key] = metricAverage(input.last90Days, metric.getter);
  }

  return result;
}

export function compareCurrentRideToHistorical(
  metric: IntelligenceMetricName,
  currentValue: number | null,
  historicalValue: number | null
): ComparisonResult {
  if (currentValue === null || historicalValue === null || historicalValue === 0) {
    return {
      metric,
      currentValue,
      historicalValue,
      absoluteDelta: null,
      percentDelta: null,
      directionVsHistorical: "insufficient"
    };
  }

  const absoluteDelta = toRounded(currentValue - historicalValue, 3);
  const percentDelta = toRounded(((currentValue - historicalValue) / Math.abs(historicalValue)) * 100, 3);

  let direction: ComparisonResult["directionVsHistorical"] = "aligned";
  if (Math.abs(percentDelta ?? 0) >= 2.5) {
    direction = (absoluteDelta ?? 0) > 0 ? "above" : "below";
  }

  return {
    metric,
    currentValue,
    historicalValue,
    absoluteDelta,
    percentDelta,
    directionVsHistorical: direction
  };
}

function trendFromPair(input: {
  recent: number | null;
  baseline: number | null;
  direction: MetricDirectionPreference;
  relativeThreshold: number;
  absoluteThreshold: number;
}): TrendDirection | null {
  const { recent, baseline, direction, relativeThreshold, absoluteThreshold } = input;
  if (recent === null || baseline === null || !Number.isFinite(recent) || !Number.isFinite(baseline)) {
    return null;
  }

  const rawDelta = recent - baseline;
  const normalizedDelta = direction === "higher_better" ? rawDelta : -rawDelta;
  const relativeDelta = baseline === 0 ? 0 : Math.abs(rawDelta) / Math.abs(baseline);

  if (Math.abs(rawDelta) < absoluteThreshold || relativeDelta < relativeThreshold) {
    return "stable";
  }

  return normalizedDelta > 0 ? "improving" : "declining";
}

function aggregateTrendVotes(votes: TrendDirection[]): TrendDirection {
  const improving = votes.filter(vote => vote === "improving").length;
  const declining = votes.filter(vote => vote === "declining").length;

  if (improving >= 3 && improving >= declining + 2) return "improving";
  if (declining >= 3 && declining >= improving + 2) return "declining";
  return "stable";
}

function trendAgainstPriorRides(records: CompletedActivityRecord[], ridesInWindow: number): TrendDirection {
  const sorted = [...records].sort((left, right) => toTimestamp(right) - toTimestamp(left));
  const recent = sorted.slice(0, ridesInWindow);
  const baseline = sorted.slice(ridesInWindow, ridesInWindow * 2);

  if (recent.length < ridesInWindow || baseline.length < ridesInWindow) {
    return "stable";
  }

  const votes: TrendDirection[] = [];
  for (const metric of METRICS) {
    const recentAvg = metricAverage(recent, metric.getter);
    const baselineAvg = metricAverage(baseline, metric.getter);
    const vote = trendFromPair({
      recent: recentAvg,
      baseline: baselineAvg,
      direction: metric.direction,
      relativeThreshold: metric.conservativeRelativeThreshold,
      absoluteThreshold: metric.conservativeAbsoluteThreshold
    });
    if (vote) votes.push(vote);
  }

  if (votes.length < 4) {
    return "stable";
  }

  return aggregateTrendVotes(votes);
}

function trendAgainstPriorDays(records: CompletedActivityRecord[], recentDays: number, baselineDays: number): TrendDirection {
  if (records.length === 0) {
    return "stable";
  }
  const sorted = [...records].sort((left, right) => toTimestamp(right) - toTimestamp(left));
  const latest = toTimestamp(sorted[0]);
  const recentStart = latest - recentDays * 24 * 60 * 60 * 1000;
  const baselineStart = latest - (recentDays + baselineDays) * 24 * 60 * 60 * 1000;

  const recent = sorted.filter(record => toTimestamp(record) >= recentStart);
  const baseline = sorted.filter(record => toTimestamp(record) >= baselineStart && toTimestamp(record) < recentStart);

  if (recent.length < 8 || baseline.length < 8) {
    return "stable";
  }

  const votes: TrendDirection[] = [];
  for (const metric of METRICS) {
    const recentAvg = metricAverage(recent, metric.getter);
    const baselineAvg = metricAverage(baseline, metric.getter);
    const vote = trendFromPair({
      recent: recentAvg,
      baseline: baselineAvg,
      direction: metric.direction,
      relativeThreshold: metric.conservativeRelativeThreshold,
      absoluteThreshold: metric.conservativeAbsoluteThreshold
    });
    if (vote) votes.push(vote);
  }

  if (votes.length < 4) {
    return "stable";
  }

  return aggregateTrendVotes(votes);
}

function highestConfidenceMetrics(records: CompletedActivityRecord[]): HighestConfidenceMetric[] {
  const totalRides = records.length;
  if (totalRides === 0) return [];

  const ranked = METRICS.map(metric => {
    const values = records
      .map(metric.getter)
      .filter((value): value is number => value !== null && Number.isFinite(value));
    if (values.length === 0) {
      return {
        metric: metric.key,
        confidenceScore: 0,
        sampleCount: 0
      };
    }

    const coverage = values.length / totalRides;
    const mean = average(values);
    const variance = average(values.map(value => (value - mean) ** 2));
    const normalizedStability = 1 / (1 + variance);
    const score = toRounded(coverage * 70 + normalizedStability * 30, 3) ?? 0;

    return {
      metric: metric.key,
      confidenceScore: score,
      sampleCount: values.length
    };
  })
    .filter(item => item.sampleCount > 0)
    .sort((left, right) => right.confidenceScore - left.confidenceScore)
    .slice(0, 3);

  return ranked;
}

export function buildAthleteIntelligenceSnapshot(records: CompletedActivityRecord[]): AthleteIntelligenceSnapshot {
  const deduped = dedupeByStableSourceHash(records);
  const windows = rollingWindows(deduped);
  const historical = buildHistoricalMetrics(deduped);
  const rolling = buildRollingMetricAverages(windows);
  const latestRide = deduped[0] ?? null;

  const comparisons = METRICS.map(metric => compareCurrentRideToHistorical(
    metric.key,
    latestRide ? metric.getter(latestRide) : null,
    historical[metric.key]
  ));

  return {
    rideCount: deduped.length,
    lastAnalysisDate: latestRide?.activityDate ?? null,
    historicalMetrics: historical,
    rollingAverages: rolling,
    currentTrend: trendAgainstPriorRides(deduped, 7),
    longTermTrend: trendAgainstPriorDays(deduped, 30, 60),
    highestConfidenceMetrics: highestConfidenceMetrics(deduped),
    comparisons
  };
}

export async function loadAthleteIntelligenceSnapshot(): Promise<AthleteIntelligenceSnapshot> {
  const records = await listCompletedActivityHistoryRecords();
  return buildAthleteIntelligenceSnapshot(records);
}
